import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import LeadmeLogo from '../assets/images/leadme-site-logo.png';

function ChangePasswordPage() {
  const navigate = useNavigate();
  const { refreshUserData } = useAuth();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Hjälpfunktion för att validera lösenord
  const validatePassword = (password) => {
    // Tillåt alla tecken (inkl. å ä ö/Unicode). Krav: minst 8 tecken, en bokstav + en siffra.
    const passwordRegex = /^(?=.*\p{L})(?=.*\d).{8,}$/u;
    return passwordRegex.test(password);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');
    setSuccessMessage('');

    // Validera att lösenorden matchar
    if (newPassword !== confirmPassword) {
      setErrorMessage('Lösenorden matchar inte');
      setLoading(false);
      return;
    }

    // Validera lösenordets format och längd
    if (!validatePassword(newPassword)) {
      setErrorMessage('Lösenordet måste vara minst 8 tecken och innehålla både bokstäver och siffror');
      setLoading(false);
      return;
    }

    try {
      await api.post('/users/change-password', {
        newPassword
      });
      
      setSuccessMessage('Lösenordet har ändrats');
      
      // Ta bort flaggan för lösenordsändring
      localStorage.removeItem('requirePasswordChange');
      
      // Uppdatera användardata
      await refreshUserData();
      
      // Efter 2 sekunder, navigera till lämplig sida baserat på användarens roll
      setTimeout(() => {
        const role = localStorage.getItem('role');
        let landingPage;
        
        switch (role) {
          case 'admin':
            landingPage = '/admin-dashboard';
            break;
          case 'kund':
            landingPage = '/myprojects';
            break;
          case 'byggkonsult':
          case 'inredningsarkitekt':
          case 'byggare':
          case 'ue':
            landingPage = '/workspace/oversikt';
            break;
          default:
            landingPage = '/';
        }
        
        navigate(landingPage);
      }, 2000);
      
    } catch (error) {
      console.error('Detailed error changing password:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        setErrorMessage(error.response.data.message || 'Kunde inte ändra lösenordet');
      } else if (error.request) {
        setErrorMessage('Kunde inte nå servern. Kontrollera din internetanslutning.');
      } else {
        setErrorMessage('Ett fel uppstod. Försök igen senare.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center bg-gray-50 px-4">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md">
        <div className="flex justify-center">
          <img
            className="h-12 w-auto"
            src={LeadmeLogo}
            alt="Leadme Logo"
          />
        </div>
        <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
          Ändra ditt lösenord
        </h2>
        <p className="text-center text-gray-600">
          Du måste ändra ditt lösenord innan du kan fortsätta.
        </p>
        
        <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded relative">
          <div className="text-sm">
            <strong>Lösenordskrav:</strong>
            <ul className="list-disc pl-5 mt-1">
              <li>Minst 8 tecken långt</li>
              <li>Måste innehålla minst en bokstav</li>
              <li>Måste innehålla minst en siffra</li>
              <li>Kan innehålla specialtecken</li>
            </ul>
          </div>
        </div>
        
        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {errorMessage}
          </div>
        )}
        
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative">
            {successMessage}
          </div>
        )}
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="new-password" className="sr-only">
                Nytt lösenord
              </label>
              <input
                id="new-password"
                name="new-password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Nytt lösenord"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="sr-only">
                Bekräfta lösenord
              </label>
              <input
                id="confirm-password"
                name="confirm-password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Bekräfta lösenord"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300"
            >
              {loading ? 'Bearbetar...' : 'Spara nytt lösenord'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ChangePasswordPage; 